// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { findStep } from "@flip/App/Widgets/CheckoutStepper/CheckoutStepper.helper";
import { StepperContext } from "@flip/App/Widgets/CheckoutStepper/StepperContext";
import { fontConfig } from "@flip/XDL/PreStyle/typography";
import { color, xScreen } from "@flip/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { useContext } from "react";
import { css } from "styled-components";

interface OwnProps {}

type Props = OwnProps;

export interface CheckoutStepperStyles {
    container?: InjectableCss;
    stepperWrapper?: InjectableCss;
    stepperItem: {
        wrapper?: InjectableCss;
        circle?: InjectableCss;
        title?: TypographyProps;
    };
}

export const styles: CheckoutStepperStyles = {
    container: {
        css: css``,
    },
    stepperWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            column-gap: 35px;
            width: 100%;

            ${xScreen("lt", "sm")} {
                column-gap: 22px;
            }

            & > li:last-child:after {
                display: none;
            }
            & > li:last-of-type {
                margin-right: 0;
            }
        `,
    },
    stepperItem: {
        wrapper: {
            css: css`
                display: flex;
                justify-content: flex-start;
                position: relative;
                cursor: default;

                ${({ isCompleted }: any) =>
                    isCompleted &&
                    css`
                        &:after {
                            background-color: ${color("common.ui2")};
                        }
                    `}

                ${({ isCompleted, isActive }: any) =>
                    (isCompleted || isActive) &&
                    css`
                        cursor: pointer;
                    `}

                ${xScreen("lte", "xs")} {
                    flex: 1 0 auto;
                }
            `,
        },
        // XNG-Changes: FLIP-896
        circle: {
            css: css`
                display: flex;
                align-items: center;
                justify-content: center;
                width: 21px;
                height: 21px;
                border: 1px solid ${color("common.border")};
                border-radius: 50%;
                background-color: ${color("common.ui1")};
                color: ${color("text")};
                margin-right: 8px;
                // For step number
                font-size: 12px;
                line-height: 20px;
                font-weight: ${fontConfig.weight.semiBold};
                letter-spacing: 0;

                ${({ isCompleted }: any) =>
                    isCompleted &&
                    css`
                        background-color: ${color("common.border")};
                    `};
                ${({ isActive }: any) =>
                    isActive &&
                    css`
                        background-color: ${color("common.border")};
                        border-color: ${color("common.ui7")};
                    `};
            `,
        },
        title: {
            size: 12,
            lineHeight: "22px",
            weight: fontConfig.weight.semiBold,
            css: css`
                white-space: nowrap;
                opacity: 0.5;

                ${({ isCompleted, isActive }: any) =>
                    (isCompleted || isActive) &&
                    css`
                        opacity: 1;
                    `};

                ${xScreen("lt", "sm")} {
                    display: none;
                }
            `,
        },
    },
};

export const CheckoutStepper: React.FC<Props> = props => {
    const { steps, currentStep, maxStep, moveToStepHandler } = useContext(StepperContext);
    const currentStepText = findStep(steps, currentStep).name;

    const StepperComponent = () => {
        const style = styles.stepperItem;
        return (
            <Typography as="ul" {...styles.stepperWrapper}>
                {steps.map((item, index) => {
                    const isCompleted = item.index < currentStep;
                    const isActive = item.index === currentStep;
                    const isClickable = item.index <= maxStep;

                    const props = { isCompleted, isActive, isClickable };
                    const className = `${isActive ? "active" : ""} ${isCompleted ? "completed" : ""}`.trim();

                    return (
                        <Typography
                            as="li"
                            {...style.wrapper}
                            {...props}
                            key={item.name}
                            className={className}
                            onClick={() => isClickable && moveToStepHandler(item.index)}
                        >
                            <Typography {...style.circle} {...props}>
                                {index + 1}
                            </Typography>
                            <Typography {...style.title} {...props}>
                                {item.name}
                            </Typography>
                        </Typography>
                    );
                })}
            </Typography>
        );
    };

    return (
        <StyledWrapper {...styles.container}>
            {/* eslint-disable-next-line spire/avoid-dynamic-translate */}
            <VisuallyHidden aria-current="step">{translate(currentStepText)}</VisuallyHidden>
            <StepperComponent />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: CheckoutStepper,
    definition: {
        group: "Checkout - Shipping",
        allowedContexts: ["CheckoutShippingPage", "CheckoutReviewAndSubmitPage"],
        displayName: "Checkout Stepper",
        fieldDefinitions: [],
    },
};

export default widgetModule;
